/**
 * Klassen, the teacher's first level (spec 10.1): their classes with label, number and student
 * count, and "Klasse anlegen". The number and the age band are assigned by the server — a
 * class number is global across all schools (spec 8.1), so the teacher only names the class.
 */

import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authedJson } from "../shared/api.js";
import type { TeacherClass } from "../shared/api-types.js";
import { germanMessage } from "../shared/errors.js";
import { Empty, ErrorLine, Loading, Panel, TButton, inputClass } from "./ui.js";

export function ClassesScreen() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const classes = useQuery({
    queryKey: ["teacher", "classes"],
    queryFn: () => authedJson<TeacherClass[]>("/teacher/classes", { subjectType: "teacher" }),
  });

  const create = useMutation({
    mutationFn: (newLabel: string) =>
      authedJson<TeacherClass>("/teacher/classes", {
        method: "POST",
        body: JSON.stringify({ label: newLabel }),
        subjectType: "teacher",
      }),
    onSuccess: async () => {
      setLabel("");
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = label.trim();
    if (trimmed) create.mutate(trimmed);
  }

  return (
    <>
      <h1 className="mt-0 mb-5 text-[26px] font-bold">Klassen</h1>
      <Panel
        title="Deine Klassen"
        actions={
          !creating && (
            <TButton variant="primary" onClick={() => setCreating(true)}>
              Klasse anlegen
            </TButton>
          )
        }
      >
        {creating && (
          <form
            onSubmit={submit}
            className="flex flex-wrap items-end gap-3 border-b border-[var(--color-line)] px-4 py-3"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-[14px] font-semibold">Bezeichnung</span>
              <input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="5a"
                maxLength={40}
                className={inputClass}
                // Safe here: the field only exists because the teacher just clicked
                // "Klasse anlegen", so focus follows the action rather than stealing it.
                autoFocus
              />
            </label>
            <TButton type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? "…" : "Anlegen"}
            </TButton>
            <TButton
              onClick={() => {
                setCreating(false);
                setLabel("");
                create.reset();
              }}
            >
              Abbrechen
            </TButton>
            {create.isError && <ErrorLine>{germanMessage(create.error)}</ErrorLine>}
          </form>
        )}

        {classes.isPending && <Loading />}
        {classes.isError && (
          <div className="px-4 py-6">
            <ErrorLine>{germanMessage(classes.error)}</ErrorLine>
          </div>
        )}
        {classes.data && classes.data.length === 0 && (
          <Empty>Noch keine Klasse. Leg mit «Klasse anlegen» die erste an.</Empty>
        )}
        {classes.data && classes.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="t-table">
              <thead>
                <tr>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Klassennummer</th>
                  <th scope="col">Schüler</th>
                </tr>
              </thead>
              <tbody>
                {classes.data.map((klass) => (
                  <tr key={klass.id}>
                    <td>
                      <Link
                        to={"/lehrer/klassen/" + klass.id}
                        className="font-semibold text-[var(--color-accent)] no-underline hover:underline"
                      >
                        {klass.label}
                      </Link>
                      {klass.isReview && (
                        <span className="ml-2 rounded-full bg-[#f5f5f4] px-2 py-0.5 text-[12px] text-[var(--color-muted)]">
                          Review-Klasse
                        </span>
                      )}
                    </td>
                    <td className="t-num">{klass.number}</td>
                    <td className="t-num">{klass.studentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
